/* ==========================================================================
   FK Feature Hotspots
   Single source of truth for active state: this.activeIndex.
   Everything else (aria-current, data-active, scroll position) is derived
   from it by render(). Nothing writes those attributes directly.
   ========================================================================== */

if (!customElements.get('fk-feature-hotspots')) {
  class FkFeatureHotspots extends HTMLElement {
    connectedCallback() {
      this.markers = Array.from(this.querySelectorAll('[data-hotspot-marker]'));
      this.cards = Array.from(this.querySelectorAll('[data-hotspot-card]'));
      this.track = this.querySelector('[data-hotspot-track]');

      if (this.markers.length === 0 || this.markers.length !== this.cards.length) return;

      this.activeIndex = 0;
      this.suppressSync = false;
      this.ratios = new Map();

      this.bindMarkers();
      this.bindCards();
      this.observeCards();
      this.bindThemeEditor();

      this.render();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.syncTimer) clearTimeout(this.syncTimer);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* ---------------------------------------------------------------- bind */

    bindMarkers() {
      this.markers.forEach((marker, index) => {
        marker.addEventListener('click', () => {
          this.activate(index, { scroll: true, focusCard: true });
        });

        // Pointer preview: highlight without stealing focus or scrolling.
        marker.addEventListener('mouseenter', () => this.activate(index));

        marker.addEventListener('keydown', (event) => this.onMarkerKeydown(event, index));
      });
    }

    bindCards() {
      this.cards.forEach((card, index) => {
        card.addEventListener('mouseenter', () => this.activate(index));
        card.addEventListener('focusin', () => this.activate(index));
      });
    }

    onMarkerKeydown(event, index) {
      const keys = {
        ArrowRight: index + 1,
        ArrowDown: index + 1,
        ArrowLeft: index - 1,
        ArrowUp: index - 1,
        Home: 0,
        End: this.markers.length - 1,
      };

      if (!(event.key in keys)) return;

      const next = Math.max(0, Math.min(this.markers.length - 1, keys[event.key]));
      if (next === index) return;

      event.preventDefault();
      this.activate(next, { scroll: true });
      this.markers[next].focus();
    }

    bindThemeEditor() {
      this.onBlockSelect = (event) => {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;

        const index = this.cards.findIndex((card) => card.dataset.blockId === blockId);
        if (index >= 0) this.activate(index, { scroll: true });
      };

      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* --------------------------------------------------------------- state */

    activate(index, options) {
      const settings = options || {};

      if (index < 0 || index >= this.cards.length) return;
      if (index === this.activeIndex && !settings.scroll && !settings.focusCard) return;

      this.activeIndex = index;
      this.render();

      if (settings.scroll) this.scrollToCard(index);
      if (settings.focusCard) this.cards[index].focus({ preventScroll: true });
    }

    render() {
      this.markers.forEach((marker, index) => {
        if (index === this.activeIndex) {
          marker.setAttribute('aria-current', 'true');
        } else {
          marker.removeAttribute('aria-current');
        }
      });

      this.cards.forEach((card, index) => {
        if (index === this.activeIndex) {
          card.setAttribute('data-active', 'true');
        } else {
          card.removeAttribute('data-active');
        }
      });
    }

    /* -------------------------------------------------------------- scroll */

    isScrollable() {
      return Boolean(this.track) && this.track.scrollWidth - this.track.clientWidth > 1;
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    scrollToCard(index) {
      if (!this.isScrollable()) return;

      const item = this.cards[index].closest('.fk-hotspots__item') || this.cards[index];
      const left = item.offsetLeft - (this.track.clientWidth - item.clientWidth) / 2;

      // Ignore observer callbacks while we drive the scroll ourselves.
      this.suppressSync = true;
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.suppressSync = false;
      }, 700);

      this.track.scrollTo({ left: left, behavior: this.scrollBehavior() });
    }

    /* Swipe → active marker. */
    observeCards() {
      if (!this.track || typeof IntersectionObserver === 'undefined') return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));

          if (this.suppressSync || !this.isScrollable()) return;

          let bestIndex = this.activeIndex;
          let bestRatio = 0;

          this.cards.forEach((card, index) => {
            const ratio = this.ratios.get(card) || 0;
            if (ratio > bestRatio) {
              bestRatio = ratio;
              bestIndex = index;
            }
          });

          if (bestRatio > 0.55 && bestIndex !== this.activeIndex) {
            this.activeIndex = bestIndex;
            this.render();
          }
        },
        { root: this.track, threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      this.cards.forEach((card) => this.observer.observe(card));
    }
  }

  customElements.define('fk-feature-hotspots', FkFeatureHotspots);
}